import { loginUrlFor, markSessionExpired } from '@/app/lib/postAuthRoute';

export class ApiError extends Error {
  code?: string;
  data?: Record<string, unknown>;
  constructor(public status: number, message: string, data?: Record<string, unknown>) {
    super(message);
    this.name = 'ApiError';
    this.data = data;
    this.code = data?.code as string | undefined;
  }
}

interface ApiFetchOptions extends RequestInit {
  skipAuthRedirect?: boolean;
}

// Une session coupee a deja renvoye vers la connexion (rechargement complet ensuite).
let sessionEndRedirected = false;

async function apiFetch<T>(path: string, options: ApiFetchOptions = {}): Promise<T> {
  const { skipAuthRedirect, ...fetchOptions } = options;
  const token = localStorage.getItem('token');

  const isFormData = fetchOptions.body instanceof FormData;

  const headers: Record<string, string> = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(fetchOptions.headers as Record<string, string>),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(path, { ...fetchOptions, headers });

  // Reponse d'une session deja fermee (deconnexion, compte supprime pendant
  // l'appel) : rien a deconnecter, et surtout pas de renvoi muet vers /login
  // (QA-41).
  const staleSession = !!token && localStorage.getItem('token') !== token;
  // Appel parti sans jeton juste apres une coupure deja renvoyee vers la
  // connexion : il ne remplace pas l'adresse qui en dit la raison (S-2).
  const afterSessionEnd = !token && sessionEndRedirected;
  if (res.status === 401 && !skipAuthRedirect && !staleSession && !afterSessionEnd) {
    // Compte suspendu (session coupee par l'API) : la connexion l'explique (recette N4).
    let body: { code?: string } = {};
    try { body = await res.json(); } catch { /* corps absent ou illisible */ }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    if (token) sessionEndRedirected = true;
    if (body.code === 'ACCOUNT_SUSPENDED') {
      window.location.href = '/login?suspendu=1';
    } else {
      // Session expiree, ou coupee par un changement de mot de passe (S-2) :
      // retour a cette page apres la connexion (QA-03).
      // La raison est aussi gardee pour l'onglet : un appel parti apres la
      // coupure, donc sans jeton, peut changer l'adresse avant le depart (S-2).
      const here = window.location;
      const passwordChanged = body.code === 'SESSION_REVOKED';
      const expired = !!token;
      if (expired) markSessionExpired(passwordChanged ? 'mot-de-passe' : 'expiree');
      window.location.href = here.pathname
        ? loginUrlFor(here, { expired, passwordChanged })
        : (passwordChanged ? '/login?raison=mot-de-passe' : expired ? '/login?raison=expiree' : '/login');
    }
    throw new ApiError(401, 'Non authentifié');
  }

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new ApiError(res.status, data.message ?? 'Une erreur est survenue', data);
  }

  if (res.status === 204) return undefined as T;

  return res.json();
}

export const api = {
  get: <T>(path: string) =>
    apiFetch<T>(path),
  post: <T>(path: string, body: unknown) =>
    apiFetch<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  postForm: <T>(path: string, form: FormData) =>
    apiFetch<T>(path, { method: 'POST', body: form }),
  postPublic: <T>(path: string, body: unknown) =>
    apiFetch<T>(path, { method: 'POST', body: JSON.stringify(body), skipAuthRedirect: true }),
  put: <T>(path: string, body: unknown) =>
    apiFetch<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    apiFetch<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: <T>(path: string, body?: unknown) =>
    apiFetch<T>(path, { method: 'DELETE', ...(body ? { body: JSON.stringify(body) } : {}) }),
  upload: async <T>(path: string, formData: FormData): Promise<T> => {
    const token = localStorage.getItem('token');
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(path, { method: 'POST', body: formData, headers });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      const fallback = res.status === 413
        ? 'Image trop volumineuse (max 10 Mo)'
        : 'Une erreur est survenue';
      throw new ApiError(res.status, data.message ?? fallback, data);
    }
    return res.json();
  },
};
